import React, { useState } from 'react';
import { Play, Square, History, PhoneCall, PhoneOff, Pause, HelpCircle } from 'lucide-react';
import HelpPage from '../../../components/common/HelpPage/HelpPage';
import '../../../components/common/SubHeader/SubHeader.css';

const CallListHeaderV2 = ({ times, timerMode, onTimerStart, onTimerStop, isTimerRunning }) => {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [showHelpTooltip, setShowHelpTooltip] = useState(true);
  const formatTime = (seconds) => {
    const hrs = String(Math.floor(seconds / 3600)).padStart(2, '0');
    const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${hrs}:${mins}:${secs}`;
  };

  const timerItems = [
    { label: 'Dialer', time: times.dialerTime, icon: <PhoneCall size={18} />, color: 'bg-blue-500' },
    { label: 'Talk Time', time: times.talkTime, icon: <PhoneCall size={18} />, color: 'bg-emerald-500' },
    { label: 'ACW', time: times.acwTime, icon: <History size={18} />, color: 'bg-amber-500' },
    { label: 'Idle', time: times.idleTime, icon: <Pause size={18} />, color: 'bg-slate-500' },
  ];

  return (
    <>
      <HelpPage 
        isOpen={isHelpOpen} 
        onClose={() => setIsHelpOpen(false)} 
        pageName="Call List" 
        pageCode="PAG10381" 
        themeColor="#0f172a" 
      />
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-6">
        <h2 className="text-xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
          ClickToCall Dialer
        </h2>
        
        {/* Timers Container */}
        <div className="flex items-center gap-3">
          {timerItems.map((item, index) => (
            <div 
              key={index}
              className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-full transition-all hover:bg-white hover:shadow-md group"
              title={item.label}
            >
              <div className={`${item.color} text-white p-1 rounded-full group-hover:scale-110 transition-transform`}>
                {item.icon}
              </div>
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:inline">
                {item.label}
              </span>
              <span className="text-sm font-mono font-bold text-slate-700">
                {formatTime(item.time)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-4">
        {/* Dialer Status Badge */}
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
          timerMode === 'greencall' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
          timerMode === 'holdcall' ? 'bg-amber-50 border-amber-200 text-amber-700' :
          'bg-slate-50 border-slate-200 text-slate-600'
        }`}>
          <div className={`w-2 h-2 rounded-full animate-pulse ${
            timerMode === 'greencall' ? 'bg-emerald-500' :
            timerMode === 'holdcall' ? 'bg-amber-500' :
            'bg-slate-400'
          }`} />
          <span className="text-sm font-bold capitalize">{timerMode === 'endcall' ? 'Inactive' : timerMode}</span>
        </div>

        {/* Action Controls */}
        <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 shadow-inner">
          {!isTimerRunning ? (
            <button 
              onClick={onTimerStart}
              className="p-2.5 bg-white text-emerald-600 rounded-lg shadow-sm hover:bg-emerald-50 transition-colors group"
              title="Start Dialer"
            >
              <Play size={20} fill="currentColor" className="group-hover:scale-110 transition-transform" />
            </button>
          ) : (
            <button 
              onClick={onTimerStop}
              className="p-2.5 bg-white text-rose-600 rounded-lg shadow-sm hover:bg-rose-50 transition-colors group"
              title="Stop Dialer"
            >
              <Square size={20} fill="currentColor" className="group-hover:scale-110 transition-transform" />
            </button>
          )}
          
          <button 
            className="p-2.5 text-slate-500 hover:text-slate-900 rounded-lg transition-colors group"
            title="History"
          >
            <History size={20} className="group-hover:rotate-12 transition-transform" />
          </button>
          
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button
              onClick={() => {
                setIsHelpOpen(true);
                setShowHelpTooltip(false);
              }}
              onMouseEnter={() => setShowHelpTooltip(true)}
              onMouseLeave={() => setShowHelpTooltip(false)}
              className="p-2.5 text-slate-500 hover:text-slate-900 rounded-lg transition-colors group"
              title="Help"
            >
              <HelpCircle size={20} className="group-hover:scale-110 transition-transform" />
            </button>
            {showHelpTooltip && (
              <div className="help-tooltip-pulse-right">
                Need Help?
                <div className="tooltip-arrow"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
    </>
  );
};

export default CallListHeaderV2;
